/**
 * Tara AI Hair & Scalp Consultation
 * Adaptive consultation Web Component — Typeform-style UI.
 *
 * Works with the Cloudflare Worker (tara-ai-consultation-worker):
 *   - AI-led: worker decides questions, question_ids are dynamic
 *   - Frontend renders from input_type + worker response, not a fixed question bank
 *   - conversation_history is source of truth, sent in full every turn
 *   - sessionStorage keys: taraConsultationSessionId, taraConsultationConversationHistory, taraConsultationAssessment
 *
 * State-management invariants:
 *   - Exactly one consultation step is active (visible + interactive) at any time.
 *   - All inactive steps are hidden + inert + aria-hidden + descendants disabled.
 *   - _setInflightUI only affects the active step.
 *   - Stale /api/question responses are discarded via monotonic _requestId.
 *   - Stale controls are destroyed before rendering the next adaptive question.
 */

const CONSULTATION_RUNTIME_DEFAULTS = Object.freeze({
  workerUrl: '',
  modelProvider: 'openai',
  resultsPageUrl: '/pages/consultation-results',
  debug: false,
  offerDiscountForFullSet: false,
  fullSetDiscountPercent: 0,
  questionStreaming: true,
  store: null,
});

function decodeConsultationHtmlEntities(value) {
  if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function decodeConsultationStringMap(value) {
  if (!value || typeof value !== 'object') return value || {};
  return Object.entries(value).reduce(function decodeMap(output, entry) {
    output[entry[0]] = decodeConsultationHtmlEntities(entry[1]);
    return output;
  }, {});
}

if (!customElements.get('tara-consultation')) {
  customElements.define('tara-consultation', class TaraConsultation extends HTMLElement {

    /* ── Lifecycle ── */

    constructor() {
      super();
      this.currentStep = 'anchor';
      this.conversationHistory = [];
      this.questionCount = 0;
      this.sessionId = null;
      this.abortController = null;
      this._pendingBridgeText = null;
      this._assessmentPromise = null;
      this._assessmentResult = null;
      this._assessmentError = null;
      this._assessmentRequestId = 0;
      this._waitExperience = null;
      this._assessmentWaitRunId = 0;
      this._inflight = false;
      this._lastQuestionComplete = false;
      this._lastAdaptiveResponse = null;
      this._prefetchedQuestion = null;
      this._prefetchedRequestId = 0;
      this._completionTracked = false;
      /* Request serialization — monotonically increasing request id */
      this._requestId = 0;
      /* Voice / transcript state */
      this._mediaRecorder = null;
      this._audioChunks = [];
      this._isRecording = false;
      this._isTranscribing = false;
      this._anchorSelectedValue = null;
      this._anchorSelectedLabel = null;
      this._email = null;
      this._contact = null;
      this._contactOffer = null;
    }

    connectedCallback() {
      let parsedConfig = {};
      try {
        parsedConfig = JSON.parse(
          document.getElementById(`TaraConsultationConfig-${this.dataset.sectionId}`).textContent
        );
      } catch (e) {
        console.error('[Tara Consultation] Config parse failed:', e);
      }

      this.config = { ...CONSULTATION_RUNTIME_DEFAULTS, ...parsedConfig };
      this.config.i18n = decodeConsultationStringMap(this.config.i18n);
      this.config.debug = this.config.debug || new URLSearchParams(window.location.search).get('consultation_debug') === '1';
      this.storeConfig = this._resolveStoreConfig(this.config.store);

      if (!this.config.workerUrl && window.TaraConsultationApi?.baseUrl) {
        this.config.workerUrl = window.TaraConsultationApi.baseUrl;
      }
      if (this.config.workerUrl) {
        this.config.workerUrl = this.config.workerUrl.replace(/\/+$/, '');
      }

      this._productCatalog = this._buildProductCatalog();

      this.progressBar = this.querySelector('.consultation-progress__bar');
      this.progressEl = this.querySelector('.consultation-progress');
      this.phaseEl = this.querySelector('.consultation-phase');
      this.backBtn = this.querySelector('[data-consultation-back]');
      this.kbdHint = this.querySelector('.consultation-kbd-hint');

      this._clickHandler = this._onClick.bind(this);
      this.addEventListener('click', this._clickHandler);
      this._keyHandler = this._onKeyDown.bind(this);
      document.addEventListener('keydown', this._keyHandler);

      this._emailForm = this.querySelector('[data-email-form]');
      this._emailSubmitHandler = this._onEmailSubmit.bind(this);
      if (this._emailForm) this._emailForm.addEventListener('submit', this._emailSubmitHandler);
      this._contactInput = this._getContactInput();
      this._contactInputHandler = this._syncContactInput.bind(this);
      if (this._contactInput) {
        this._contactInput.addEventListener('input', this._contactInputHandler);
        this._syncContactInput();
      }

      this._initIntake();
      this._restoreState();
      this._resetViewportOnLoad();
      this._pageShowHandler = this._resetViewportOnLoad.bind(this);
      window.addEventListener('pageshow', this._pageShowHandler);

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this._sectionSelectHandler = (e) => {
          if (e.detail.sectionId === this.dataset.sectionId) this._showStep('anchor');
        };
        document.addEventListener('shopify:section:select', this._sectionSelectHandler);
      }

      this._updateProgress();
      this._applyDocumentTitle();
      this._deferShellReady();
      this._log('init', { step: this.currentStep, sessionId: this.sessionId });
    }

    disconnectedCallback() {
      if (this._clickHandler) this.removeEventListener('click', this._clickHandler);
      document.removeEventListener('keydown', this._keyHandler);
      if (this._emailForm && this._emailSubmitHandler) this._emailForm.removeEventListener('submit', this._emailSubmitHandler);
      if (this._contactInput && this._contactInputHandler) this._contactInput.removeEventListener('input', this._contactInputHandler);
      if (this._sectionSelectHandler) document.removeEventListener('shopify:section:select', this._sectionSelectHandler);
      if (this._pageShowHandler) window.removeEventListener('pageshow', this._pageShowHandler);
      if (this._shellReadyCleanup) this._shellReadyCleanup();
      if (this._shellReadyTimer) window.clearTimeout(this._shellReadyTimer);
      this.abortController?.abort();
      this._assessmentAbort?.abort();
      this._stopVoiceRecording();
    }

    /* ── Debug Logging ── */

    _log(action, detail) {
      if (this.config && this.config.debug) {
        console.log(`[Tara Consultation] ${action}`, detail || '');
      }
    }

    _buildMonitoringMeta(meta = {}) {
      const locale = String(this._getUiLocale?.() || this.config?.locale || '').trim();
      const latestTurn = this.conversationHistory[this.conversationHistory.length - 1] || {};
      const questionId = String(this._lastAdaptiveResponse?.question_id || latestTurn.question_id || '').trim();

      return {
        route: meta.route,
        level: meta.level,
        error_code: meta.error_code || meta.errorCode || '',
        status_code: meta.status_code != null ? meta.status_code : meta.statusCode,
        request_duration_ms: meta.request_duration_ms != null ? meta.request_duration_ms : meta.durationMs,
        fingerprint: meta.fingerprint,
        tags: {
          locale: locale || undefined,
          question_id: questionId || undefined,
          consultation_step: this.currentStep || undefined,
          consultation_provider: this.config?.modelProvider || undefined,
          ...((meta.tags && typeof meta.tags === 'object') ? meta.tags : {}),
        },
        extra: {
          question_count: Number.isFinite(this.questionCount) ? this.questionCount : undefined,
          request_id: Number.isFinite(this._requestId) ? this._requestId : undefined,
          worker_error: meta.worker_error || meta.workerError || undefined,
          ...((meta.extra && typeof meta.extra === 'object') ? meta.extra : {}),
        },
      };
    }

    _captureMonitoringException(error, meta = {}) {
      if (!window.TaraConsultationMonitoring || !error) return;
      window.TaraConsultationMonitoring.captureException(error, this._buildMonitoringMeta(meta));
    }

    _captureMonitoringMessage(message, meta = {}) {
      if (!window.TaraConsultationMonitoring || !message) return;
      window.TaraConsultationMonitoring.captureMessage(message, this._buildMonitoringMeta(meta));
    }

    _startMonitoringTimer(name, meta = {}) {
      if (!window.TaraConsultationMonitoring || typeof window.TaraConsultationMonitoring.startTimer !== 'function') {
        return function noopTimer() { return 0; };
      }
      return window.TaraConsultationMonitoring.startTimer(name, this._buildMonitoringMeta(meta));
    }

    async _buildApiError(response, fallbackMessage) {
      if (window.TaraConsultationApi && typeof window.TaraConsultationApi.buildApiError === 'function') {
        return window.TaraConsultationApi.buildApiError(response, fallbackMessage);
      }

      let payload = null;
      try {
        payload = await response.clone().json();
      } catch (error) {
        payload = null;
      }

      const message = payload && typeof payload.error === 'string' && payload.error.trim()
        ? payload.error.trim()
        : `${fallbackMessage || 'API error'} (${response.status})`;
      const apiError = new Error(message);
      apiError.name = 'ConsultationApiError';
      apiError.status = response.status;
      apiError.workerError = payload && typeof payload.error === 'string' ? payload.error.trim() : '';
      apiError.errorCode = payload && typeof payload.error_code === 'string' ? payload.error_code.trim() : '';
      apiError.code = apiError.errorCode;
      return apiError;
    }

    _applyDocumentTitle() {
      const consultationTitle = this.config?.i18n?.serviceConsultation;
      if (!consultationTitle || typeof document === 'undefined') return;

      const currentTitle = (document.title || '').trim();
      if (!currentTitle) {
        document.title = consultationTitle;
        return;
      }

      const separatorMatch = currentTitle.match(/\s+(\||-|–)\s+/);
      const separator = separatorMatch ? ` ${separatorMatch[1]} ` : ' | ';
      const parts = currentTitle.split(/\s+(?:\||-|–)\s+/).filter(Boolean);
      if (!parts.length) {
        document.title = consultationTitle;
        return;
      }
      if (parts[0] === consultationTitle) return;

      parts[0] = consultationTitle;
      document.title = parts.join(separator);
    }
    _deferShellReady() {
      const reveal = () => {
        if (this.dataset.ready === 'true') return;
        if (this._shellReadyCleanup) this._shellReadyCleanup();
        if (this._shellReadyTimer) {
          window.clearTimeout(this._shellReadyTimer);
          this._shellReadyTimer = null;
        }
        this.dataset.ready = 'true';
        this.setAttribute('aria-busy', 'false');
      };

      const stylesheet = [...document.querySelectorAll('link[href*="tara-consultation.css"]')]
        .find((link) => (link.getAttribute('href') || '').includes('tara-consultation.css'));

      if (!stylesheet || stylesheet.sheet) {
        requestAnimationFrame(reveal);
        return;
      }

      const onLoad = () => reveal();
      stylesheet.addEventListener('load', onLoad, { once: true });
      this._shellReadyCleanup = () => {
        stylesheet.removeEventListener('load', onLoad);
        this._shellReadyCleanup = null;
      };
      this._shellReadyTimer = window.setTimeout(reveal, 1500);
    }

    /* ── State Management ── */

    _getStoredSessionId() {
      return window.TaraConsultationSession && typeof window.TaraConsultationSession.getStoredSessionId === 'function'
        ? window.TaraConsultationSession.getStoredSessionId()
        : null;
    }

    _setStoredSessionId(id) {
      if (window.TaraConsultationSession && typeof window.TaraConsultationSession.setStoredSessionId === 'function') {
        window.TaraConsultationSession.setStoredSessionId(id);
      }
    }

    _getConversationHistory() {
      return window.TaraConsultationSession && typeof window.TaraConsultationSession.getConversationHistory === 'function'
        ? window.TaraConsultationSession.getConversationHistory()
        : [];
    }

    _setConversationHistory(history) {
      if (window.TaraConsultationSession && typeof window.TaraConsultationSession.setConversationHistory === 'function') {
        window.TaraConsultationSession.setConversationHistory(history);
      }
    }

    _persistProgressState(lastQuestionId = '') {
      if (window.TaraConsultationSession && typeof window.TaraConsultationSession.setProgressState === 'function') {
        window.TaraConsultationSession.setProgressState({
          sessionId: this._ensureSessionId(),
          history: this.conversationHistory,
          lastQuestionId
        });
      }
    }

    _getStoredAssessment() {
      return window.TaraConsultationSession && typeof window.TaraConsultationSession.getStoredAssessment === 'function'
        ? window.TaraConsultationSession.getStoredAssessment()
        : null;
    }

    _setStoredAssessment(assessment) {
      if (window.TaraConsultationSession && typeof window.TaraConsultationSession.setStoredAssessment === 'function') {
        window.TaraConsultationSession.setStoredAssessment(assessment);
      }
    }

    _hasSeenOnboarding() {
      return window.TaraConsultationSession && typeof window.TaraConsultationSession.hasSeenOnboarding === 'function'
        ? window.TaraConsultationSession.hasSeenOnboarding()
        : false;
    }

    _setOnboardingSeen() {
      this._anchorOnboardingDismissed = true;
      if (window.TaraConsultationSession && typeof window.TaraConsultationSession.setOnboardingSeen === 'function') {
        window.TaraConsultationSession.setOnboardingSeen();
      }
    }

    _normalizeOfferPayload(offer) {
      if (!offer || typeof offer !== 'object') return null;

      const activation = offer.activation && typeof offer.activation === 'object'
        ? {
          status: typeof offer.activation.status === 'string' ? offer.activation.status : '',
          code: typeof offer.activation.code === 'string' ? offer.activation.code : '',
          checkout_url: typeof offer.activation.checkout_url === 'string' ? offer.activation.checkout_url : '',
          discount_url: typeof offer.activation.discount_url === 'string' ? offer.activation.discount_url : '',
          created_at: typeof offer.activation.created_at === 'string' ? offer.activation.created_at : '',
          expires_at: typeof offer.activation.expires_at === 'string' ? offer.activation.expires_at : '',
          reason: typeof offer.activation.reason === 'string' ? offer.activation.reason : ''
        }
        : null;

      return {
        ...offer,
        activation
      };
    }

    _mergeOfferIntoAssessment(result, offer) {
      if (!result || !offer) return result;
      return {
        ...result,
        offer: this._normalizeOfferPayload(offer)
      };
    }

    _ensureSessionId() {
      if (!this.sessionId) {
        this.sessionId = window.TaraConsultationSession && typeof window.TaraConsultationSession.ensureSessionId === 'function'
          ? window.TaraConsultationSession.ensureSessionId(this._getStoredSessionId())
          : (this._getStoredSessionId()
            || (crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2)));
        this._setStoredSessionId(this.sessionId);
      }
      return this.sessionId;
    }

    _serializeConversationTurn(questionId, questionText, answerValue, answerLabel, inputType) {
      return {
        question_id: questionId,
        question: questionText,
        answer: answerValue,
        answer_label: answerLabel,
        input_type: inputType
      };
    }

    /* ── Product Catalog ── */

    _buildProductCatalog() {
      let products = [];
      try {
        const el = document.getElementById(`TaraConsultationProductCatalog-${this.dataset.sectionId}`);
        if (el) products = JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[Tara Consultation] Could not parse product catalog:', e);
        return [];
      }

      /* Enrich bundle_components from the family mapping (Liquid outputs
         bundle_handle per product but leaves bundle_components empty) */
      let families = {};
      try {
        const fEl = document.getElementById(`TaraConsultationBundleFamilies-${this.dataset.sectionId}`);
        if (fEl) families = JSON.parse(fEl.textContent);
      } catch (e) { /* non-critical */ }

      return products.map(p => ({
        ...p,
        bundle_components: p.is_bundle ? (families[p.handle] || []) : []
      }));
    }

    /* ── Facebook Helpers ── */

    _readCookie(name) {
      const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : '';
    }

    _getFacebookTrackingPayload() {
      return {
        event_id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        event_source_url: window.location.href,
        fbp: this._readCookie('_fbp'),
        fbc: this._readCookie('_fbc')
      };
    }

    /* ── Event Handlers ── */

    _onClick(e) {
      if (window.TaraConsultationInteractions && typeof window.TaraConsultationInteractions.onClick === 'function') {
        window.TaraConsultationInteractions.onClick(this, e);
      }
    }

    _onKeyDown(e) {
      if (window.TaraConsultationInteractions && typeof window.TaraConsultationInteractions.onKeyDown === 'function') {
        window.TaraConsultationInteractions.onKeyDown(this, e);
      }
    }

    /* Option Selection */

    _selectOption(option) {
      if (window.TaraConsultationInteractions && typeof window.TaraConsultationInteractions.selectOption === 'function') {
        window.TaraConsultationInteractions.selectOption(this, option);
      }
    }

    /* Anchor Chip Selection */

    _selectChip(chip) {
      if (window.TaraConsultationInteractions && typeof window.TaraConsultationInteractions.selectChip === 'function') {
        window.TaraConsultationInteractions.selectChip(this, chip);
      }
    }

    _dismissAnchorVoiceNudge() {
      if (window.TaraConsultationInteractions && typeof window.TaraConsultationInteractions.dismissAnchorVoiceNudge === 'function') {
        window.TaraConsultationInteractions.dismissAnchorVoiceNudge(this);
      }
    }

    _resetAnchorVoiceNudge() {
      if (window.TaraConsultationInteractions && typeof window.TaraConsultationInteractions.resetAnchorVoiceNudge === 'function') {
        window.TaraConsultationInteractions.resetAnchorVoiceNudge(this);
      }
    }

    _advanceAnchorOnboarding(direction) {
      if (window.TaraConsultationInteractions && typeof window.TaraConsultationInteractions.advanceAnchorOnboarding === 'function') {
        window.TaraConsultationInteractions.advanceAnchorOnboarding(this, direction);
      }
    }

    _syncAnchorOnboarding() {
      if (window.TaraConsultationInteractions && typeof window.TaraConsultationInteractions.syncAnchorOnboarding === 'function') {
        window.TaraConsultationInteractions.syncAnchorOnboarding(this);
      }
    }

    _autoResizeTextarea(textarea) {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.autoResizeTextarea === 'function') {
        window.TaraConsultationInput.autoResizeTextarea(this, textarea);
      }
    }

    _syncAdaptiveTextControls() {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.syncAdaptiveTextControls === 'function') {
        window.TaraConsultationInput.syncAdaptiveTextControls(this);
      }
    }

    _renderAdaptiveSuggestions(suggestions) {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.renderAdaptiveSuggestions === 'function') {
        window.TaraConsultationInput.renderAdaptiveSuggestions(this, suggestions);
      }
    }

    /* Submit anchor with option only (no free text) */
    _submitAnchorOptionOnly(value, label) {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.submitAnchorOptionOnly === 'function') {
        window.TaraConsultationInput.submitAnchorOptionOnly(this, value, label);
      }
    }

    /* Intake and Voice */

    _initIntake() {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.initIntake === 'function') {
        window.TaraConsultationInput.initIntake(this);
      }
    }

    /* First-screen intake submit
       Builds the clean first-turn payload:
       - If only text: answer = text, input_type = free_text
       - If text + option: answer = text, selected_value/label preserved
       - If only option: handled by _submitAnchorOptionOnly */
    _submitAnchorIntake() {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.submitAnchorIntake === 'function') {
        window.TaraConsultationInput.submitAnchorIntake(this);
      }
    }

    /* Adaptive free-text submit */
    _submitAdaptiveText() {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.submitAdaptiveText === 'function') {
        window.TaraConsultationInput.submitAdaptiveText(this);
      }
    }

    /* Voice recording: MediaRecorder to worker upload */

    async _toggleVoiceRecording(micBtn) {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.toggleVoiceRecording === 'function') {
        await window.TaraConsultationInput.toggleVoiceRecording(this, micBtn);
      }
    }

    _stopVoiceRecording() {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.stopVoiceRecording === 'function') {
        window.TaraConsultationInput.stopVoiceRecording(this);
      }
    }

    /* Send raw audio blob directly to /api/question as multipart form-data.
       Worker will transcribe internally and process the turn.
       Voice uploads always target the absolute worker URL to avoid Shopify
       proxy routing issues (Shopify app proxies may not forward multipart). */
    async _submitVoiceNote(blob, micBtn) {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.submitVoiceNote === 'function') {
        await window.TaraConsultationInput.submitVoiceNote(this, blob, micBtn);
      }
    }

    /* Resolve the target URL for voice uploads.
       Always returns an absolute URL pointing directly at the Cloudflare Worker
       so that multipart form-data is not routed through Shopify app proxies. */
    _getVoiceUploadUrl() {
      return window.TaraConsultationInput && typeof window.TaraConsultationInput.getVoiceUploadUrl === 'function'
        ? window.TaraConsultationInput.getVoiceUploadUrl(this)
        : null;
    }

    _showIntakeHint(micBtn, key, isError) {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.showIntakeHint === 'function') {
        window.TaraConsultationInput.showIntakeHint(this, micBtn, key, isError);
      }
    }

    _hideIntakeHint(micBtn) {
      if (window.TaraConsultationInput && typeof window.TaraConsultationInput.hideIntakeHint === 'function') {
        window.TaraConsultationInput.hideIntakeHint(this, micBtn);
      }
    }

    _showStep(name, direction = 'forward') {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.showStep === 'function') {
        window.TaraConsultationUI.showStep(this, name, direction);
      }
    }

    /* Disable all interactive controls within a step */
    _disableStepControls(stepEl) {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.disableStepControls === 'function') {
        window.TaraConsultationUI.disableStepControls(stepEl);
      }
    }

    /* Re-enable interactive controls within a step */
    _enableStepControls(stepEl) {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.enableStepControls === 'function') {
        window.TaraConsultationUI.enableStepControls(stepEl);
      }
    }

    _goBack() {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.goBack === 'function') {
        window.TaraConsultationUI.goBack(this);
      }
    }

    /* Progress */

    _updateProgress() {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.updateProgress === 'function') {
        window.TaraConsultationUI.updateProgress(this);
      }
    }

    _getPhaseLabel() {
      return window.TaraConsultationUI && typeof window.TaraConsultationUI.getPhaseLabel === 'function'
        ? window.TaraConsultationUI.getPhaseLabel(this)
        : '';
    }

    /* API: Fetch Next Question */

    _prefetchNextQuestion() {
      return window.TaraConsultationQuestionFlow && typeof window.TaraConsultationQuestionFlow.prefetchNextQuestion === 'function'
        ? window.TaraConsultationQuestionFlow.prefetchNextQuestion(this)
        : null;
    }

    _startQuestionFetch() {
      return window.TaraConsultationQuestionFlow && typeof window.TaraConsultationQuestionFlow.startQuestionFetch === 'function'
        ? window.TaraConsultationQuestionFlow.startQuestionFetch(this)
        : Promise.resolve(null);
    }

    async _requestNextQuestion(direction = 'forward', requestPromise = null, requestId = null) {
      if (window.TaraConsultationQuestionFlow && typeof window.TaraConsultationQuestionFlow.requestNextQuestion === 'function') {
        await window.TaraConsultationQuestionFlow.requestNextQuestion(this, direction, requestPromise, requestId);
      }
    }

    async _advanceFromPrefetch(direction = 'forward') {
      if (window.TaraConsultationQuestionFlow && typeof window.TaraConsultationQuestionFlow.advanceFromPrefetch === 'function') {
        await window.TaraConsultationQuestionFlow.advanceFromPrefetch(this, direction);
      }
    }

    async _fetchNextQuestion(direction = 'forward') {
      if (window.TaraConsultationQuestionFlow && typeof window.TaraConsultationQuestionFlow.fetchNextQuestion === 'function') {
        await window.TaraConsultationQuestionFlow.fetchNextQuestion(this, direction);
      }
    }

    _normalizeCopy(value) {
      return window.TaraConsultationUI && typeof window.TaraConsultationUI.normalizeCopy === 'function'
        ? window.TaraConsultationUI.normalizeCopy(this, value)
        : '';
    }

    _normalizeCopyToken(value) {
      return window.TaraConsultationUI && typeof window.TaraConsultationUI.normalizeCopyToken === 'function'
        ? window.TaraConsultationUI.normalizeCopyToken(this, value)
        : '';
    }

    _setOptionalCopy(el, value) {
      return window.TaraConsultationUI && typeof window.TaraConsultationUI.setOptionalCopy === 'function'
        ? window.TaraConsultationUI.setOptionalCopy(this, el, value)
        : '';
    }

    _renderAdaptiveCopy(data = {}) {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.renderAdaptiveCopy === 'function') {
        window.TaraConsultationUI.renderAdaptiveCopy(this, data);
      }
    }

    _renderAdaptiveQuestion(data) {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.renderAdaptiveQuestion === 'function') {
        window.TaraConsultationUI.renderAdaptiveQuestion(this, data);
      }
    }

    /* Voice Retry UI
       Worker returned needs_voice_retry: true because voice was empty or weak.
       Render text-first retry with suggestion chips on the adaptive step. */
    _renderVoiceRetry(data) {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.renderVoiceRetry === 'function') {
        window.TaraConsultationUI.renderVoiceRetry(this, data);
      }
    }

    /* Double-Submit Protection UI
       Scoped to the active step only and never re-enables inactive controls. */

    _setInflightUI(active) {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.setInflightUI === 'function') {
        window.TaraConsultationUI.setInflightUI(this, active);
      }
    }

    /* Bridge Text */

    _showBridgeText() {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.showBridgeText === 'function') {
        window.TaraConsultationUI.showBridgeText(this);
      }
    }

    _getUiLocale() {
      if (window.TaraConsultationStore && typeof window.TaraConsultationStore.getUiLocale === 'function') {
        return window.TaraConsultationStore.getUiLocale(this.config?.locale);
      }
      const locale = (this.config?.locale || document.documentElement.lang || '').toString().trim();
      return locale || 'en';
    }

    _normalizeWorkerLocale(value) {
      return window.TaraConsultationStore && typeof window.TaraConsultationStore.normalizeWorkerLocale === 'function'
        ? window.TaraConsultationStore.normalizeWorkerLocale(value)
        : 'en';
    }

    _resolveStoreConfig(rawStore = null) {
      return window.TaraConsultationStore && typeof window.TaraConsultationStore.normalizeStoreConfig === 'function'
        ? window.TaraConsultationStore.normalizeStoreConfig(rawStore)
        : {
          key: '',
          market: '',
          brandName: '',
          contactCaptureMode: 'required',
          allowEmailContact: true,
          allowMobileContact: false,
          whatsappNumberE164: '',
          whatsappUrl: ''
        };
    }

    _getStoreConfig() {
      if (!this.storeConfig) this.storeConfig = this._resolveStoreConfig(this.config?.store);
      return this.storeConfig;
    }

    _isContactCaptureRequired() {
      return this._getStoreConfig().contactCaptureMode === 'required';
    }

    _shouldSkipContactCapture() {
      return this._getStoreConfig().contactCaptureMode === 'skip';
    }

    _getContactUiCopy() {
      return window.TaraConsultationContact && typeof window.TaraConsultationContact.getUiCopy === 'function'
        ? window.TaraConsultationContact.getUiCopy(this)
        : {
          description: '',
          placeholder: '',
          optinText: '',
          skipLabel: 'Skip for now',
          captureLabel: 'Where should we send it?',
          channels: [],
          deliveryLabel: 'Sending your consultation to',
          deliverySkippedLabel: 'Finalizing your consultation'
        };
    }

    _configureContactStep() {
      if (window.TaraConsultationContact && typeof window.TaraConsultationContact.configureStep === 'function') {
        window.TaraConsultationContact.configureStep(this);
      }
    }

    _buildStorePayload() {
      return window.TaraConsultationStore && typeof window.TaraConsultationStore.buildStorePayload === 'function'
        ? window.TaraConsultationStore.buildStorePayload(this._getStoreConfig())
        : {
          key: null,
          market: null,
          brandName: null,
          contactCaptureMode: 'required',
          allowEmailContact: true,
          allowMobileContact: false,
          whatsappNumberE164: null,
          whatsappUrl: null
        };
    }

    _buildWorkerContextPayload(locale = this._getUiLocale()) {
      return window.TaraConsultationStore && typeof window.TaraConsultationStore.buildWorkerContextPayload === 'function'
        ? window.TaraConsultationStore.buildWorkerContextPayload({
          locale,
          store: this._getStoreConfig()
        })
        : {};
    }

    _buildLatestAnswerPayload() {
      const lastTurn = this.conversationHistory[this.conversationHistory.length - 1];
      if (!lastTurn || typeof lastTurn !== 'object') return null;

      const payload = {
        question: lastTurn.question || '',
        answer: lastTurn.answer || '',
        answer_label: lastTurn.answer_label || lastTurn.answer || '',
        input_type: lastTurn.input_type || 'free_text'
      };

      if (lastTurn.question_id) payload.question_id = lastTurn.question_id;
      if (lastTurn.selected_value) payload.selected_value = lastTurn.selected_value;
      if (lastTurn.selected_label) payload.selected_label = lastTurn.selected_label;

      return payload;
    }

    _getContactInput() {
      return this.querySelector('[data-contact-input]') || this.querySelector('.consultation-email-input');
    }

    _isValidEmail(value) {
      return window.TaraConsultationStore && typeof window.TaraConsultationStore.isValidEmail === 'function'
        ? window.TaraConsultationStore.isValidEmail(value)
        : false;
    }

    _normalizePhone(value) {
      return window.TaraConsultationStore && typeof window.TaraConsultationStore.normalizePhone === 'function'
        ? window.TaraConsultationStore.normalizePhone(value)
        : null;
    }

    _parseContactValue(rawValue, { allowEmpty = false } = {}) {
      return window.TaraConsultationStore && typeof window.TaraConsultationStore.parseContactValue === 'function'
        ? window.TaraConsultationStore.parseContactValue(rawValue, {
          store: this._getStoreConfig(),
          allowEmpty
        })
        : { valid: false, empty: false, type: 'email', value: '' };
    }

    _getInvalidContactMessage(contact) {
      return window.TaraConsultationContact && typeof window.TaraConsultationContact.getInvalidMessage === 'function'
        ? window.TaraConsultationContact.getInvalidMessage(this, contact)
        : 'Enter a valid contact value.';
    }

    _syncContactInput() {
      if (window.TaraConsultationContact && typeof window.TaraConsultationContact.syncInput === 'function') {
        window.TaraConsultationContact.syncInput(this);
      }
    }

    /* ── Early Assessment ── */

    _fireAssessmentEarly() {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.fireAssessmentEarly === 'function') {
        return window.TaraConsultationCompletion.fireAssessmentEarly(this);
      }
      return null;
    }

    _resetAssessmentState() {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.resetAssessmentRetryState === 'function') {
        window.TaraConsultationCompletion.resetAssessmentRetryState(this);
      }
    }

    async _fetchAssessmentAsync() {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.fetchAssessmentAsync === 'function') {
        return window.TaraConsultationCompletion.fetchAssessmentAsync(this);
      }
      return null;
    }

    /* Debug Panel */

    _normalizeWaitExperience(waitExperience) {
      return window.TaraConsultationWait && typeof window.TaraConsultationWait.normalizeWaitExperience === 'function'
        ? window.TaraConsultationWait.normalizeWaitExperience(this, waitExperience)
        : {
          title: '',
          story: '',
          whyWait: '',
          moments: [],
          closingLine: ''
        };
    }

    _renderAssessmentWait(waitExperience) {
      if (window.TaraConsultationWait && typeof window.TaraConsultationWait.renderAssessmentWait === 'function') {
        window.TaraConsultationWait.renderAssessmentWait(this, waitExperience);
      }
    }

    _enterContactCapture(waitExperience, direction = 'forward') {
      if (window.TaraConsultationWait && typeof window.TaraConsultationWait.enterContactCapture === 'function') {
        window.TaraConsultationWait.enterContactCapture(this, waitExperience, direction);
      }
    }

    _continueWithoutContact() {
      if (window.TaraConsultationWait && typeof window.TaraConsultationWait.continueWithoutContact === 'function') {
        window.TaraConsultationWait.continueWithoutContact(this);
      }
    }

    _enterAssessmentWait(waitExperience, direction = 'forward') {
      if (window.TaraConsultationWait && typeof window.TaraConsultationWait.enterAssessmentWait === 'function') {
        window.TaraConsultationWait.enterAssessmentWait(this, waitExperience, direction);
      }
    }

    async _waitForAssessmentAndRedirect() {
      if (window.TaraConsultationWait && typeof window.TaraConsultationWait.waitForAssessmentAndRedirect === 'function') {
        await window.TaraConsultationWait.waitForAssessmentAndRedirect(this);
      }
    }

    async _ensureAssessmentWaitMinimum() {
      if (window.TaraConsultationWait && typeof window.TaraConsultationWait.ensureAssessmentWaitMinimum === 'function') {
        await window.TaraConsultationWait.ensureAssessmentWaitMinimum(this);
      }
    }

    async _completeAssessmentWaitProgress() {
      if (window.TaraConsultationWait && typeof window.TaraConsultationWait.completeAssessmentWaitProgress === 'function') {
        await window.TaraConsultationWait.completeAssessmentWaitProgress(this);
      }
    }

    _renderDebug(data) {
      const panel = this.querySelector('[data-consultation-debug]');
      if (!panel || !this.config.debug) {
        if (panel) panel.hidden = true;
        return;
      }

      const debugData = { ...data };
      panel.hidden = false;
      const d = debugData._debug || {};
      const timing = d.duration_ms ? `<strong style="color:#22d3ee">${d.duration_ms}ms</strong>` : '';
      const tokens = d.usage ? ` | In: ${d.usage.input_tokens || '?'} Out: ${d.usage.output_tokens || '?'}` : '';
      panel.innerHTML =
        `<details class="consultation-debug__details" open>` +
        `<summary class="consultation-debug__summary">Debug: Q${this.questionCount + 1} ${timing ? '— ' + timing : ''}</summary>` +
        (d.error ? `<div class="consultation-debug__meta"><strong style="color:#ef4444">ERROR: ${this._escapeHtml(d.error)}</strong></div>` : '') +
        `<div class="consultation-debug__meta">${this._escapeHtml(d.model || '?')} | effort: ${this._escapeHtml(d.reasoningEffort || '?')} | candidates: ${this._escapeHtml(JSON.stringify(d.candidates || []))}${tokens}</div>` +
        `<pre class="consultation-debug__json">${this._escapeHtml(JSON.stringify(debugData, null, 2))}</pre>` +
        `</details>`;
    }

    /* ── Email Submit ── */

    async _onEmailSubmit(e) {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.onEmailSubmit === 'function') {
        await window.TaraConsultationCompletion.onEmailSubmit(this, e);
      }
    }

    _identifyKlaviyo(email, subscribe) {
      if (window.TaraConsultationAnalytics && typeof window.TaraConsultationAnalytics.identifyKlaviyo === 'function') {
        window.TaraConsultationAnalytics.identifyKlaviyo(this, email, subscribe);
      }
    }

    _normalizeOfferDiscountPercent(value) {
      return window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.normalizeOfferDiscountPercent === 'function'
        ? window.TaraConsultationCompletion.normalizeOfferDiscountPercent(value)
        : 0;
    }

    _getOfferSettingsPayload() {
      return window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.getOfferSettingsPayload === 'function'
        ? window.TaraConsultationCompletion.getOfferSettingsPayload(this)
        : {
          offer_discount_for_full_set: false,
          full_set_discount_percent: 0
        };
    }

    async _sendContactCapture(contact, subscribe, fbPayload) {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.sendContactCapture === 'function') {
        return window.TaraConsultationCompletion.sendContactCapture(this, contact, subscribe, fbPayload);
      }
      return null;
    }

    _buildStoredContactPayload(contact) {
      return window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.buildStoredContactPayload === 'function'
        ? window.TaraConsultationCompletion.buildStoredContactPayload(contact)
        : null;
    }

    _buildStoredAssessmentPayload(result, contact) {
      return window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.buildStoredAssessmentPayload === 'function'
        ? window.TaraConsultationCompletion.buildStoredAssessmentPayload(this, result, contact)
        : { assessment: result, store: this._buildStorePayload(), contact: null };
    }

    /* Analytics */

    _track(eventName, data = {}) {
      if (window.TaraConsultationAnalytics && typeof window.TaraConsultationAnalytics.track === 'function') {
        window.TaraConsultationAnalytics.track(this, eventName, data);
      }
    }

    /* ── Store Result & Redirect ── */

    _storeAndRedirect(result, contact) {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.storeAndRedirect === 'function') {
        window.TaraConsultationCompletion.storeAndRedirect(this, result, contact);
      }
    }

    _goToResults() {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.goToResults === 'function') {
        window.TaraConsultationCompletion.goToResults(this);
      }
    }

    /* Assessment Loading Animation */

    _animateLoadingSteps() {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.animateLoadingSteps === 'function') {
        window.TaraConsultationCompletion.animateLoadingSteps(this);
      }
    }

    /* Retake */

    _retake() {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.retake === 'function') {
        window.TaraConsultationCompletion.retake(this);
      }
    }

    /* State Persistence */

    _clearStoredState() {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.clearStoredState === 'function') {
        window.TaraConsultationCompletion.clearStoredState(this);
      }
    }

    _restoreState() {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.restoreState === 'function') {
        window.TaraConsultationCompletion.restoreState(this);
      }
    }

    _resetViewportOnLoad() {
      if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.resetViewportOnLoad === 'function') {
        window.TaraConsultationCompletion.resetViewportOnLoad(this);
      }
    }

    /* Error State */

    _clearErrorStates() {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.clearErrorStates === 'function') {
        window.TaraConsultationUI.clearErrorStates(this);
      }
    }

    _getQuestionRetryLabel() {
      return window.TaraConsultationUI && typeof window.TaraConsultationUI.getQuestionRetryLabel === 'function'
        ? window.TaraConsultationUI.getQuestionRetryLabel(this)
        : 'Try again';
    }

    _getQuestionErrorMessage(error) {
      return window.TaraConsultationUI && typeof window.TaraConsultationUI.getQuestionErrorMessage === 'function'
        ? window.TaraConsultationUI.getQuestionErrorMessage(this, error)
        : 'We could not continue your consultation right now. Try again and we will continue from the same path.';
    }

    _renderQuestionRequestError(direction = 'forward', error = null) {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.renderQuestionRequestError === 'function') {
        window.TaraConsultationUI.renderQuestionRequestError(this, direction, error);
      }
    }

    _renderAssessmentFailure(onRetry, error = null) {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.renderAssessmentFailure === 'function') {
        window.TaraConsultationUI.renderAssessmentFailure(this, onRetry, error);
      }
    }

    _renderErrorState(message, options = {}) {
      if (window.TaraConsultationUI && typeof window.TaraConsultationUI.renderErrorState === 'function') {
        window.TaraConsultationUI.renderErrorState(this, message, options);
      }
    }

    /* Utilities */

    _resolveLocalizedPath(url) {
      if (!url || !url.startsWith('/')) return url;

      const root = window.Shopify?.routes?.root;
      if (!root || root === '/' || url === root || url.startsWith(root)) return url;

      return `${root.replace(/\/$/, '')}${url}`;
    }

    _escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    _escapeAttr(str) {
      if (!str) return '';
      return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  });
}

